// banking/src/pages/onboarding/corporate/corporate-translations.ts

export type Language = 'es' | 'en' | 'fr' | 'de' | 'it' | 'pt' | 'nl' | 'ru' | 'ar';

interface TranslationSet {
  es: string;
  en: string;
  fr: string;
  de: string;
  it: string;
  pt: string;
  nl: string;
  ru: string;
  ar: string;
}

export const corporateTranslations: Record<string, TranslationSet> = {
  // General UI
  step_of: {
    es: 'Paso {current} de {total}',
    en: 'Step {current} of {total}',
    fr: 'Étape {current} sur {total}',
    de: 'Schritt {current} von {total}',
    it: 'Passo {current} di {total}',
    pt: 'Passo {current} de {total}',
    nl: 'Stap {current} van {total}',
    ru: 'Шаг {current} из {total}',
    ar: 'الخطوة {current} من {total}'
  },

  previous_button: {
    es: 'Anterior',
    en: 'Previous',
    fr: 'Précédent',
    de: 'Zurück',
    it: 'Precedente',
    pt: 'Anterior',
    nl: 'Vorige',
    ru: 'Назад',
    ar: 'السابق'
  },

  next_button: {
    es: 'Siguiente',
    en: 'Next',
    fr: 'Suivant',
    de: 'Weiter',
    it: 'Avanti',
    pt: 'Próximo',
    nl: 'Volgende',
    ru: 'Далее',
    ar: 'التالي'
  },

  save_button: {
    es: 'Guardar',
    en: 'Save',
    fr: 'Enregistrer',
    de: 'Speichern',
    it: 'Salva',
    pt: 'Salvar',
    nl: 'Opslaan',
    ru: 'Сохранить',
    ar: 'حفظ'
  },

  submit_button: {
    es: 'Completar Registro',
    en: 'Complete Registration',
    fr: 'Compléter l\'inscription',
    de: 'Registrierung abschließen',
    it: 'Completa registrazione',
    pt: 'Completar registro',
    nl: 'Registratie voltooien',
    ru: 'Завершить регистрацию',
    ar: 'إكمال التسجيل'
  },

  required_field: {
    es: 'Este campo es obligatorio',
    en: 'This field is required',
    fr: 'Ce champ est obligatoire',
    de: 'Dieses Feld ist erforderlich',
    it: 'Questo campo è obbligatorio',
    pt: 'Este campo é obrigatório',
    nl: 'Dit veld is verplicht',
    ru: 'Это поле обязательно',
    ar: 'هذا الحقل مطلوب'
  },

  invalid_email: {
    es: 'Correo electrónico inválido',
    en: 'Invalid email address',
    fr: 'Adresse e-mail invalide',
    de: 'Ungültige E-Mail-Adresse',
    it: 'Indirizzo email non valido',
    pt: 'Endereço de e-mail inválido',
    nl: 'Ongeldig e-mailadres',
    ru: 'Неверный адрес электронной почты',
    ar: 'عنوان بريد إلكتروني غير صالح'
  },

  loading: {
    es: 'Cargando...',
    en: 'Loading...',
    fr: 'Chargement...',
    de: 'Wird geladen...',
    it: 'Caricamento...',
    pt: 'Carregando...',
    nl: 'Laden...',
    ru: 'Загрузка...',
    ar: 'جار التحميل...'
  },

  // STEP 1: Company Basic Information
  step1_title: {
    es: 'Información Básica de la Empresa',
    en: 'Company Basic Information',
    fr: 'Informations de base sur l\'entreprise',
    de: 'Grundlegende Unternehmensinformationen',
    it: 'Informazioni di base sull\'azienda',
    pt: 'Informações básicas da empresa',
    nl: 'Basisinformatie bedrijf',
    ru: 'Основная информация о компании',
    ar: 'المعلومات الأساسية للشركة'
  },

  step1_subtitle: {
    es: 'Por favor, proporcione la información básica de su empresa',
    en: 'Please provide your company\'s basic information',
    fr: 'Veuillez fournir les informations de base de votre entreprise',
    de: 'Bitte geben Sie die grundlegenden Informationen Ihres Unternehmens an',
    it: 'Si prega di fornire le informazioni di base della propria azienda',
    pt: 'Por favor, forneça as informações básicas de sua empresa',
    nl: 'Geef de basisinformatie van uw bedrijf op',
    ru: 'Пожалуйста, предоставьте основную информацию о вашей компании',
    ar: 'يرجى تقديم المعلومات الأساسية لشركتك'
  },

  company_name_label: {
    es: 'Nombre Comercial',
    en: 'Company Name',
    fr: 'Nom commercial',
    de: 'Firmenname',
    it: 'Nome commerciale',
    pt: 'Nome comercial',
    nl: 'Bedrijfsnaam',
    ru: 'Коммерческое название',
    ar: 'الاسم التجاري'
  },

  company_name_placeholder: {
    es: 'Ingrese el nombre comercial de la empresa',
    en: 'Enter the company\'s trade name',
    fr: 'Entrez le nom commercial de l\'entreprise',
    de: 'Geben Sie den Handelsnamen des Unternehmens ein',
    it: 'Inserisci il nome commerciale dell\'azienda',
    pt: 'Digite o nome comercial da empresa',
    nl: 'Voer de handelsnaam van het bedrijf in',
    ru: 'Введите торговое название компании',
    ar: 'أدخل الاسم التجاري للشركة'
  },

  legal_name_label: {
    es: 'Razón Social',
    en: 'Legal Name',
    fr: 'Raison sociale',
    de: 'Rechtliche Bezeichnung',
    it: 'Ragione sociale',
    pt: 'Razão social',
    nl: 'Juridische naam',
    ru: 'Юридическое название',
    ar: 'الاسم القانوني'
  },

  legal_name_placeholder: {
    es: 'Ingrese la razón social completa',
    en: 'Enter the full legal name',
    fr: 'Entrez la raison sociale complète',
    de: 'Geben Sie die vollständige rechtliche Bezeichnung ein',
    it: 'Inserisci la ragione sociale completa',
    pt: 'Digite a razão social completa',
    nl: 'Voer de volledige juridische naam in',
    ru: 'Введите полное юридическое название',
    ar: 'أدخل الاسم القانوني الكامل'
  },

  company_type_label: {
    es: 'Tipo de Empresa',
    en: 'Company Type',
    fr: 'Type d\'entreprise',
    de: 'Unternehmenstyp',
    it: 'Tipo di azienda',
    pt: 'Tipo de empresa',
    nl: 'Type bedrijf',
    ru: 'Тип компании',
    ar: 'نوع الشركة'
  },

  company_type_placeholder: {
    es: 'Seleccione el tipo de empresa',
    en: 'Select company type',
    fr: 'Sélectionnez le type d\'entreprise',
    de: 'Wählen Sie den Unternehmenstyp',
    it: 'Seleziona il tipo di azienda',
    pt: 'Selecione o tipo de empresa',
    nl: 'Selecteer type bedrijf',
    ru: 'Выберите тип компании',
    ar: 'حدد نوع الشركة'
  },

  tax_id_label: {
    es: 'NIF/CIF/RUC',
    en: 'Tax ID',
    fr: 'Numéro fiscal',
    de: 'Steuernummer',
    it: 'Partita IVA',
    pt: 'CNPJ/NIF',
    nl: 'BTW-nummer',
    ru: 'Налоговый номер',
    ar: 'الرقم الضريبي'
  },

  tax_id_placeholder: {
    es: 'Ingrese el número de identificación fiscal',
    en: 'Enter tax identification number',
    fr: 'Entrez le numéro d\'identification fiscale',
    de: 'Geben Sie die Steuernummer ein',
    it: 'Inserisci il numero di partita IVA',
    pt: 'Digite o número de identificação fiscal',
    nl: 'Voer het BTW-nummer in',
    ru: 'Введите налоговый идентификационный номер',
    ar: 'أدخل رقم التعريف الضريبي'
  },

  tax_country_label: {
    es: 'País de Registro Fiscal',
    en: 'Tax Registration Country',
    fr: 'Pays d\'enregistrement fiscal',
    de: 'Land der Steuerregistrierung',
    it: 'Paese di registrazione fiscale',
    pt: 'País de registro fiscal',
    nl: 'Land van fiscale registratie',
    ru: 'Страна налоговой регистрации',
    ar: 'بلد التسجيل الضريبي'
  },

  registration_number_label: {
    es: 'Número de Registro Mercantil',
    en: 'Registration Number',
    fr: 'Numéro d\'enregistrement',
    de: 'Registrierungsnummer',
    it: 'Numero di registrazione',
    pt: 'Número de registro',
    nl: 'Registratienummer',
    ru: 'Регистрационный номер',
    ar: 'رقم التسجيل'
  },

  registration_number_placeholder: {
    es: 'Ingrese el número de registro mercantil',
    en: 'Enter commercial registration number',
    fr: 'Entrez le numéro d\'enregistrement commercial',
    de: 'Geben Sie die Handelsregisternummer ein',
    it: 'Inserisci il numero di registrazione commerciale',
    pt: 'Digite o número de registro comercial',
    nl: 'Voer het handelsregisternummer in',
    ru: 'Введите коммерческий регистрационный номер',
    ar: 'أدخل رقم التسجيل التجاري'
  },

  registration_country_label: {
    es: 'País de Registro',
    en: 'Registration Country',
    fr: 'Pays d\'enregistrement',
    de: 'Registrierungsland',
    it: 'Paese di registrazione',
    pt: 'País de registro',
    nl: 'Registratieland',
    ru: 'Страна регистрации',
    ar: 'بلد التسجيل'
  },

  registration_date_label: {
    es: 'Fecha de Registro',
    en: 'Registration Date',
    fr: 'Date d\'enregistrement',
    de: 'Registrierungsdatum',
    it: 'Data di registrazione',
    pt: 'Data de registro',
    nl: 'Registratiedatum',
    ru: 'Дата регистрации',
    ar: 'تاريخ التسجيل'
  },

  incorporation_date_label: {
    es: 'Fecha de Constitución',
    en: 'Incorporation Date',
    fr: 'Date de constitution',
    de: 'Gründungsdatum',
    it: 'Data di costituzione',
    pt: 'Data de constituição',
    nl: 'Oprichtingsdatum',
    ru: 'Дата учреждения',
    ar: 'تاريخ التأسيس'
  },

  business_sector_label: {
    es: 'Sector Empresarial (NAICS)',
    en: 'Business Sector (NAICS)',
    fr: 'Secteur d\'activité (NAICS)',
    de: 'Wirtschaftszweig (NAICS)',
    it: 'Settore aziendale (NAICS)',
    pt: 'Setor empresarial (NAICS)',
    nl: 'Bedrijfssector (NAICS)',
    ru: 'Сектор бизнеса (NAICS)',
    ar: 'القطاع التجاري (NAICS)'
  },

  business_sector_placeholder: {
    es: 'Busque y seleccione el código NAICS',
    en: 'Search and select NAICS code',
    fr: 'Recherchez et sélectionnez le code NAICS',
    de: 'Suchen und wählen Sie den NAICS-Code',
    it: 'Cerca e seleziona il codice NAICS',
    pt: 'Pesquise e selecione o código NAICS',
    nl: 'Zoek en selecteer NAICS-code',
    ru: 'Найдите и выберите код NAICS',
    ar: 'ابحث واختر رمز NAICS'
  },

  business_activity_label: {
    es: 'Descripción de Actividad Empresarial',
    en: 'Business Activity Description',
    fr: 'Description de l\'activité',
    de: 'Beschreibung der Geschäftstätigkeit',
    it: 'Descrizione dell\'attività',
    pt: 'Descrição da atividade empresarial',
    nl: 'Beschrijving bedrijfsactiviteit',
    ru: 'Описание деятельности',
    ar: 'وصف النشاط التجاري'
  },

  business_activity_placeholder: {
    es: 'Describa brevemente la actividad principal de la empresa',
    en: 'Briefly describe the company\'s main activity',
    fr: 'Décrivez brièvement l\'activité principale de l\'entreprise',
    de: 'Beschreiben Sie kurz die Haupttätigkeit des Unternehmens',
    it: 'Descriva brevemente l\'attività principale dell\'azienda',
    pt: 'Descreva brevemente a atividade principal da empresa',
    nl: 'Beschrijf kort de hoofdactiviteit van het bedrijf',
    ru: 'Кратко опишите основную деятельность компании',
    ar: 'صف بإيجاز النشاط الرئيسي للشركة'
  },

  business_type_label: {
    es: 'Tipo de Negocio',
    en: 'Business Type',
    fr: 'Type d\'entreprise',
    de: 'Geschäftstyp',
    it: 'Tipo di attività',
    pt: 'Tipo de negócio',
    nl: 'Type bedrijf',
    ru: 'Тип бизнеса',
    ar: 'نوع العمل'
  },

  business_description_label: {
    es: 'Descripción del Negocio (Opcional)',
    en: 'Business Description (Optional)',
    fr: 'Description de l\'entreprise (Optionnel)',
    de: 'Geschäftsbeschreibung (Optional)',
    it: 'Descrizione dell\'attività (Opzionale)',
    pt: 'Descrição do negócio (Opcional)',
    nl: 'Bedrijfsbeschrijving (Optioneel)',
    ru: 'Описание бизнеса (Необязательно)',
    ar: 'وصف العمل (اختياري)'
  },

  business_description_text_label: {
    es: 'Descripción del Negocio',
    en: 'Business Description',
    fr: 'Description de l\'entreprise',
    de: 'Geschäftsbeschreibung',
    it: 'Descrizione dell\'attività',
    pt: 'Descrição do negócio',
    nl: 'Bedrijfsbeschrijving',
    ru: 'Описание бизнеса',
    ar: 'وصف العمل'
  },

  industry_type_label: {
    es: 'Tipo de Industria (Opcional)',
    en: 'Industry Type (Optional)',
    fr: 'Type d\'industrie (Optionnel)',
    de: 'Branchentyp (Optional)',
    it: 'Tipo di industria (Opzionale)',
    pt: 'Tipo de indústria (Opcional)',
    nl: 'Type industrie (Optioneel)',
    ru: 'Тип отрасли (Необязательно)',
    ar: 'نوع الصناعة (اختياري)'
  },

  // STEP 2: Company Contact and Address
  step2_title: {
    es: 'Contacto y Dirección de la Empresa',
    en: 'Company Contact and Address',
    fr: 'Contact et adresse de l\'entreprise',
    de: 'Kontakt und Adresse des Unternehmens',
    it: 'Contatto e indirizzo dell\'azienda',
    pt: 'Contato e endereço da empresa',
    nl: 'Contact en adres van het bedrijf',
    ru: 'Контакт и адрес компании',
    ar: 'الاتصال وعنوان الشركة'
  },

  step2_subtitle: {
    es: 'Proporcione los datos de contacto y dirección de la empresa',
    en: 'Provide the company\'s contact details and address',
    fr: 'Fournissez les coordonnées et l\'adresse de l\'entreprise',
    de: 'Geben Sie die Kontaktdaten und Adresse des Unternehmens an',
    it: 'Fornisci i dettagli di contatto e l\'indirizzo dell\'azienda',
    pt: 'Forneça os dados de contato e endereço da empresa',
    nl: 'Geef de contactgegevens en het adres van het bedrijf op',
    ru: 'Укажите контактные данные и адрес компании',
    ar: 'قدم تفاصيل الاتصال وعنوان الشركة'
  },

  company_email_label: {
    es: 'Correo Electrónico Corporativo',
    en: 'Corporate Email',
    fr: 'E-mail professionnel',
    de: 'Unternehmens-E-Mail',
    it: 'Email aziendale',
    pt: 'E-mail corporativo',
    nl: 'Zakelijk e-mailadres',
    ru: 'Корпоративная почта',
    ar: 'البريد الإلكتروني للشركة'
  },

  company_phone_label: {
    es: 'Teléfono de la Empresa',
    en: 'Company Phone',
    fr: 'Téléphone de l\'entreprise',
    de: 'Firmentelefon',
    it: 'Telefono aziendale',
    pt: 'Telefone da empresa',
    nl: 'Bedrijfstelefoon',
    ru: 'Телефон компании',
    ar: 'هاتف الشركة'
  },

  phone_country_code_label: {
    es: 'Código de País',
    en: 'Country Code',
    fr: 'Code pays',
    de: 'Landesvorwahl',
    it: 'Prefisso paese',
    pt: 'Código do país',
    nl: 'Landcode',
    ru: 'Код страны',
    ar: 'رمز البلد'
  },

  phone_number_label: {
    es: 'Número de Teléfono',
    en: 'Phone Number',
    fr: 'Numéro de téléphone',
    de: 'Telefonnummer',
    it: 'Numero di telefono',
    pt: 'Número de telefone',
    nl: 'Telefoonnummer',
    ru: 'Номер телефона',
    ar: 'رقم الهاتف'
  },

  phone_extension_label: {
    es: 'Extensión (Opcional)',
    en: 'Extension (Optional)',
    fr: 'Extension (Optionnel)',
    de: 'Durchwahl (Optional)',
    it: 'Interno (Opzionale)',
    pt: 'Ramal (Opcional)',
    nl: 'Toestel (Optioneel)',
    ru: 'Добавочный (Необязательно)',
    ar: 'تحويلة (اختياري)'
  },

  address_line1_label: {
    es: 'Dirección',
    en: 'Address',
    fr: 'Adresse',
    de: 'Adresse',
    it: 'Indirizzo',
    pt: 'Endereço',
    nl: 'Adres',
    ru: 'Адрес',
    ar: 'العنوان'
  },

  address_line1_placeholder: {
    es: 'Calle, número, piso, etc.',
    en: 'Street, number, floor, etc.',
    fr: 'Rue, numéro, étage, etc.',
    de: 'Straße, Nummer, Etage, etc.',
    it: 'Via, numero, piano, ecc.',
    pt: 'Rua, número, andar, etc.',
    nl: 'Straat, nummer, verdieping, etc.',
    ru: 'Улица, номер, этаж и т.д.',
    ar: 'الشارع، الرقم، الطابق، إلخ.'
  },

  website_label: {
    es: 'Sitio Web (Opcional)',
    en: 'Website (Optional)',
    fr: 'Site web (Optionnel)',
    de: 'Webseite (Optional)',
    it: 'Sito web (Opzionale)',
    pt: 'Website (Opcional)',
    nl: 'Website (Optioneel)',
    ru: 'Веб-сайт (Необязательно)',
    ar: 'الموقع الإلكتروني (اختياري)'
  },

  website_placeholder: {
    es: 'www.ejemplo.com',
    en: 'www.example.com',
    fr: 'www.exemple.com',
    de: 'www.beispiel.com',
    it: 'www.esempio.com',
    pt: 'www.exemplo.com',
    nl: 'www.voorbeeld.nl',
    ru: 'www.пример.ru',
    ar: 'www.example.com'
  },

  // STEP 3: Company Documents
  step3_title: {
    es: 'Documentos de la Empresa',
    en: 'Company Documents',
    fr: 'Documents de l\'entreprise',
    de: 'Unternehmensdokumente',
    it: 'Documenti dell\'azienda',
    pt: 'Documentos da empresa',
    nl: 'Bedrijfsdocumenten',
    ru: 'Документы компании',
    ar: 'وثائق الشركة'
  },

  step3_subtitle: {
    es: 'Suba los documentos oficiales de la empresa (formato PDF, JPG o PNG, máximo 10MB cada uno)',
    en: 'Upload the official company documents (PDF, JPG or PNG format, max 10MB each)',
    fr: 'Téléchargez les documents officiels de l\'entreprise (format PDF, JPG ou PNG, max 10 Mo chacun)',
    de: 'Laden Sie die offiziellen Unternehmensdokumente hoch (PDF, JPG oder PNG, max. 10 MB pro Datei)',
    it: 'Carica i documenti ufficiali dell\'azienda (formato PDF, JPG o PNG, max 10 MB ciascuno)',
    pt: 'Carregue os documentos oficiais da empresa (formato PDF, JPG ou PNG, máximo 10MB cada)',
    nl: 'Upload de officiële bedrijfsdocumenten (PDF, JPG of PNG formaat, max 10 MB elk)',
    ru: 'Загрузите официальные документы компании (формат PDF, JPG или PNG, макс. 10 МБ каждый)',
    ar: 'قم بتحميل الوثائق الرسمية للشركة (تنسيق PDF أو JPG أو PNG، بحد أقصى 10 ميجابايت لكل منها)'
  },

  business_registration_label: {
    es: 'Certificado de Constitución',
    en: 'Certificate of Incorporation',
    fr: 'Certificat de constitution',
    de: 'Gründungsurkunde',
    it: 'Certificato di costituzione',
    pt: 'Certificado de constituição',
    nl: 'Oprichtingscertificaat',
    ru: 'Свидетельство о регистрации',
    ar: 'شهادة التأسيس'
  },

  articles_of_incorporation_label: {
    es: 'Estatutos Sociales',
    en: 'Articles of Incorporation',
    fr: 'Statuts',
    de: 'Satzung',
    it: 'Statuto sociale',
    pt: 'Estatutos sociais',
    nl: 'Statuten',
    ru: 'Устав',
    ar: 'النظام الأساسي'
  },

  business_utility_bill_label: {
    es: 'Factura de Servicios de la Empresa',
    en: 'Company Utility Bill',
    fr: 'Facture de services de l\'entreprise',
    de: 'Betriebskostenrechnung',
    it: 'Bolletta aziendale',
    pt: 'Conta de serviços da empresa',
    nl: 'Bedrijfsrekening',
    ru: 'Счет за коммунальные услуги компании',
    ar: 'فاتورة خدمات الشركة'
  },

  upload_file_button: {
    es: 'Subir Archivo',
    en: 'Upload File',
    fr: 'Télécharger le fichier',
    de: 'Datei hochladen',
    it: 'Carica file',
    pt: 'Carregar arquivo',
    nl: 'Bestand uploaden',
    ru: 'Загрузить файл',
    ar: 'تحميل ملف'
  },

  file_uploaded: {
    es: 'Archivo subido',
    en: 'File uploaded',
    fr: 'Fichier téléchargé',
    de: 'Datei hochgeladen',
    it: 'File caricato',
    pt: 'Arquivo carregado',
    nl: 'Bestand geüpload',
    ru: 'Файл загружен',
    ar: 'تم تحميل الملف'
  },

  remove_file: {
    es: 'Eliminar',
    en: 'Remove',
    fr: 'Supprimer',
    de: 'Entfernen',
    it: 'Rimuovi',
    pt: 'Remover',
    nl: 'Verwijderen',
    ru: 'Удалить',
    ar: 'إزالة'
  },

  drag_drop_text: {
    es: 'Arrastre un archivo aquí o haga clic para seleccionar',
    en: 'Drag a file here or click to select',
    fr: 'Glissez un fichier ici ou cliquez pour sélectionner',
    de: 'Ziehen Sie eine Datei hierher oder klicken Sie zum Auswählen',
    it: 'Trascina un file qui o fai clic per selezionare',
    pt: 'Arraste um arquivo aqui ou clique para selecionar',
    nl: 'Sleep een bestand hierheen of klik om te selecteren',
    ru: 'Перетащите файл сюда или нажмите для выбора',
    ar: 'اسحب ملفًا هنا أو انقر للتحديد'
  },

  // STEP 4: Representatives
  step4_title: {
    es: 'Representantes de la Empresa',
    en: 'Company Representatives',
    fr: 'Représentants de l\'entreprise',
    de: 'Unternehmensvertreter',
    it: 'Rappresentanti dell\'azienda',
    pt: 'Representantes da empresa',
    nl: 'Bedrijfsvertegenwoordigers',
    ru: 'Представители компании',
    ar: 'ممثلو الشركة'
  },

  step4_subtitle: {
    es: 'Agregue al menos un representante legal de la empresa',
    en: 'Add at least one legal representative of the company',
    fr: 'Ajoutez au moins un représentant légal de l\'entreprise',
    de: 'Fügen Sie mindestens einen gesetzlichen Vertreter des Unternehmens hinzu',
    it: 'Aggiungi almeno un rappresentante legale dell\'azienda',
    pt: 'Adicione pelo menos um representante legal da empresa',
    nl: 'Voeg ten minste één wettelijk vertegenwoordiger van het bedrijf toe',
    ru: 'Добавьте хотя бы одного законного представителя компании',
    ar: 'أضف ممثلاً قانونيًا واحدًا على الأقل للشركة'
  },

  logout_button: {
    es: 'Cerrar Sesión',
    en: 'Logout',
    fr: 'Déconnexion',
    de: 'Abmelden',
    it: 'Esci',
    pt: 'Sair',
    nl: 'Uitloggen',
    ru: 'Выйти',
    ar: 'تسجيل الخروج'
  },

  logout_confirm: {
    es: '¿Está seguro que desea cerrar sesión? Su progreso se guardará.',
    en: 'Are you sure you want to logout? Your progress will be saved.',
    fr: 'Êtes-vous sûr de vouloir vous déconnecter? Votre progrès sera sauvegardé.',
    de: 'Möchten Sie sich wirklich abmelden? Ihr Fortschritt wird gespeichert.',
    it: 'Sei sicuro di voler uscire? Il tuo progresso sarà salvato.',
    pt: 'Tem certeza de que deseja sair? Seu progresso será salvo.',
    nl: 'Weet u zeker dat u wilt uitloggen? Uw voortgang wordt opgeslagen.',
    ru: 'Вы уверены, что хотите выйти? Ваш прогресс будет сохранен.',
    ar: 'هل أنت متأكد من أنك تريد تسجيل الخروج؟ سيتم حفظ تقدمك.'
  },

  finalize_representatives_button: {
    es: 'Finalizar Representantes',
    en: 'Finalize Representatives',
    fr: 'Finaliser les représentants',
    de: 'Vertreter abschließen',
    it: 'Finalizza rappresentanti',
    pt: 'Finalizar representantes',
    nl: 'Vertegenwoordigers voltooien',
    ru: 'Завершить представителей',
    ar: 'إنهاء الممثلين'
  },

  complete_representative_button: {
    es: 'Completar Representante e Ir a Lista',
    en: 'Complete Representative and Go to List',
    fr: 'Terminer le représentant et aller à la liste',
    de: 'Vertreter abschließen und zur Liste gehen',
    it: 'Completa rappresentante e vai alla lista',
    pt: 'Completar representante e ir para a lista',
    nl: 'Vertegenwoordiger voltooien en naar lijst gaan',
    ru: 'Завершить представителя и перейти к списку',
    ar: 'إكمال الممثل والانتقال إلى القائمة'
  },

  edit_representatives_button: {
    es: 'Editar Representantes',
    en: 'Edit Representatives',
    fr: 'Modifier les représentants',
    de: 'Vertreter bearbeiten',
    it: 'Modifica rappresentanti',
    pt: 'Editar representantes',
    nl: 'Vertegenwoordigers bewerken',
    ru: 'Редактировать представителей',
    ar: 'تحرير الممثلين'
  },

  add_representative_button: {
    es: 'Agregar Representante',
    en: 'Add Representative',
    fr: 'Ajouter un représentant',
    de: 'Vertreter hinzufügen',
    it: 'Aggiungi rappresentante',
    pt: 'Adicionar representante',
    nl: 'Vertegenwoordiger toevoegen',
    ru: 'Добавить представителя',
    ar: 'إضافة ممثل'
  },

  representatives_added: {
    es: 'Representantes agregados',
    en: 'Representatives added',
    fr: 'Représentants ajoutés',
    de: 'Vertreter hinzugefügt',
    it: 'Rappresentanti aggiunti',
    pt: 'Representantes adicionados',
    nl: 'Vertegenwoordigers toegevoegd',
    ru: 'Представители добавлены',
    ar: 'الممثلون المضافون'
  },

  edit_representative: {
    es: 'Editar',
    en: 'Edit',
    fr: 'Modifier',
    de: 'Bearbeiten',
    it: 'Modifica',
    pt: 'Editar',
    nl: 'Bewerken',
    ru: 'Редактировать',
    ar: 'تعديل'
  },

  delete_representative: {
    es: 'Eliminar',
    en: 'Delete',
    fr: 'Supprimer',
    de: 'Löschen',
    it: 'Elimina',
    pt: 'Excluir',
    nl: 'Verwijderen',
    ru: 'Удалить',
    ar: 'حذف'
  },

  min_one_representative: {
    es: 'Debe agregar al menos un representante',
    en: 'You must add at least one representative',
    fr: 'Vous devez ajouter au moins un représentant',
    de: 'Sie müssen mindestens einen Vertreter hinzufügen',
    it: 'Devi aggiungere almeno un rappresentante',
    pt: 'Você deve adicionar pelo menos um representante',
    nl: 'U moet ten minste één vertegenwoordiger toevoegen',
    ru: 'Вы должны добавить хотя бы одного представителя',
    ar: 'يجب عليك إضافة ممثل واحد على الأقل'
  },

  // STEP 5: Representative Data
  step5_title: {
    es: 'Datos del Representante',
    en: 'Representative Data',
    fr: 'Données du représentant',
    de: 'Vertreterdaten',
    it: 'Dati del rappresentante',
    pt: 'Dados do representante',
    nl: 'Gegevens vertegenwoordiger',
    ru: 'Данные представителя',
    ar: 'بيانات الممثل'
  },

  step5_subtitle: {
    es: 'Complete la información del representante',
    en: 'Complete the representative\'s information',
    fr: 'Complétez les informations du représentant',
    de: 'Vervollständigen Sie die Informationen des Vertreters',
    it: 'Completa le informazioni del rappresentante',
    pt: 'Complete as informações do representante',
    nl: 'Vul de gegevens van de vertegenwoordiger in',
    ru: 'Заполните информацию о представителе',
    ar: 'أكمل معلومات الممثل'
  },

  relationship_type_label: {
    es: 'Tipo de Relación',
    en: 'Relationship Type',
    fr: 'Type de relation',
    de: 'Beziehungstyp',
    it: 'Tipo di relazione',
    pt: 'Tipo de relacionamento',
    nl: 'Type relatie',
    ru: 'Тип отношений',
    ar: 'نوع العلاقة'
  },

  role_label: {
    es: 'Cargo/Rol',
    en: 'Position/Role',
    fr: 'Poste/Rôle',
    de: 'Position/Rolle',
    it: 'Posizione/Ruolo',
    pt: 'Cargo/Função',
    nl: 'Functie/Rol',
    ru: 'Должность/Роль',
    ar: 'المنصب/الدور'
  },

  role_placeholder: {
    es: 'Ej: CEO, Director, Accionista, etc.',
    en: 'E.g.: CEO, Director, Shareholder, etc.',
    fr: 'Ex: PDG, Directeur, Actionnaire, etc.',
    de: 'Z.B.: CEO, Direktor, Aktionär, usw.',
    it: 'Es.: CEO, Direttore, Azionista, ecc.',
    pt: 'Ex: CEO, Diretor, Acionista, etc.',
    nl: 'Bijv.: CEO, Directeur, Aandeelhouder, enz.',
    ru: 'Напр.: CEO, Директор, Акционер и т.д.',
    ar: 'على سبيل المثال: الرئيس التنفيذي، المدير، المساهم، إلخ.'
  },

  is_ultimate_beneficiary_label: {
    es: '¿Es Beneficiario Final?',
    en: 'Is Ultimate Beneficiary?',
    fr: 'Est bénéficiaire final?',
    de: 'Ist wirtschaftlich Berechtigter?',
    it: 'È beneficiario finale?',
    pt: 'É beneficiário final?',
    nl: 'Is uiteindelijk belanghebbende?',
    ru: 'Является конечным бенефициаром?',
    ar: 'هل هو المستفيد النهائي؟'
  },

  ownership_percentage_label: {
    es: 'Porcentaje de Propiedad (%)',
    en: 'Ownership Percentage (%)',
    fr: 'Pourcentage de propriété (%)',
    de: 'Eigentumsanteil (%)',
    it: 'Percentuale di proprietà (%)',
    pt: 'Porcentagem de propriedade (%)',
    nl: 'Eigendomspercentage (%)',
    ru: 'Доля владения (%)',
    ar: 'نسبة الملكية (%)'
  },

  first_name_label: {
    es: 'Nombre',
    en: 'First Name',
    fr: 'Prénom',
    de: 'Vorname',
    it: 'Nome',
    pt: 'Nome',
    nl: 'Voornaam',
    ru: 'Имя',
    ar: 'الاسم الأول'
  },

  last_name_label: {
    es: 'Primer Apellido',
    en: 'Last Name',
    fr: 'Nom de famille',
    de: 'Nachname',
    it: 'Cognome',
    pt: 'Sobrenome',
    nl: 'Achternaam',
    ru: 'Фамилия',
    ar: 'اسم العائلة'
  },

  middle_name_label: {
    es: 'Segundo Nombre',
    en: 'Middle Name',
    fr: 'Deuxième prénom',
    de: 'Zweiter Vorname',
    it: 'Secondo nome',
    pt: 'Nome do meio',
    nl: 'Tweede voornaam',
    ru: 'Второе имя',
    ar: 'الاسم الأوسط'
  },

  second_last_name_label: {
    es: 'Segundo Apellido',
    en: 'Second Last Name',
    fr: 'Deuxième nom de famille',
    de: 'Zweiter Nachname',
    it: 'Secondo cognome',
    pt: 'Segundo sobrenome',
    nl: 'Tweede achternaam',
    ru: 'Вторая фамилия',
    ar: 'اسم العائلة الثاني'
  },

  gender_label: {
    es: 'Género',
    en: 'Gender',
    fr: 'Genre',
    de: 'Geschlecht',
    it: 'Genere',
    pt: 'Gênero',
    nl: 'Geslacht',
    ru: 'Пол',
    ar: 'الجنس'
  },

  birth_date_label: {
    es: 'Fecha de Nacimiento',
    en: 'Date of Birth',
    fr: 'Date de naissance',
    de: 'Geburtsdatum',
    it: 'Data di nascita',
    pt: 'Data de nascimento',
    nl: 'Geboortedatum',
    ru: 'Дата рождения',
    ar: 'تاريخ الميلاد'
  },

  birth_country_label: {
    es: 'País de Nacimiento',
    en: 'Country of Birth',
    fr: 'Pays de naissance',
    de: 'Geburtsland',
    it: 'Paese di nascita',
    pt: 'País de nascimento',
    nl: 'Geboorteland',
    ru: 'Страна рождения',
    ar: 'بلد الميلاد'
  },

  nationality_label: {
    es: 'Nacionalidad',
    en: 'Nationality',
    fr: 'Nationalité',
    de: 'Staatsangehörigkeit',
    it: 'Nazionalità',
    pt: 'Nacionalidade',
    nl: 'Nationaliteit',
    ru: 'Гражданство',
    ar: 'الجنسية'
  },

  citizenship_label: {
    es: 'Ciudadanía',
    en: 'Citizenship',
    fr: 'Citoyenneté',
    de: 'Staatsbürgerschaft',
    it: 'Cittadinanza',
    pt: 'Cidadania',
    nl: 'Burgerschap',
    ru: 'Гражданство',
    ar: 'المواطنة'
  },

  residence_country_label: {
    es: 'País de Residencia',
    en: 'Country of Residence',
    fr: 'Pays de résidence',
    de: 'Wohnsitzland',
    it: 'Paese di residenza',
    pt: 'País de residência',
    nl: 'Land van verblijf',
    ru: 'Страна проживания',
    ar: 'بلد الإقامة'
  },

  second_nationality_label: {
    es: 'Segunda Nacionalidad (Opcional)',
    en: 'Second Nationality (Optional)',
    fr: 'Deuxième nationalité (Optionnel)',
    de: 'Zweite Staatsangehörigkeit (Optional)',
    it: 'Seconda nazionalità (Opzionale)',
    pt: 'Segunda nacionalidade (Opcional)',
    nl: 'Tweede nationaliteit (Optioneel)',
    ru: 'Второе гражданство (Необязательно)',
    ar: 'الجنسية الثانية (اختياري)'
  },

  document_type_label: {
    es: 'Tipo de Documento',
    en: 'Document Type',
    fr: 'Type de document',
    de: 'Dokumenttyp',
    it: 'Tipo di documento',
    pt: 'Tipo de documento',
    nl: 'Documenttype',
    ru: 'Тип документа',
    ar: 'نوع الوثيقة'
  },

  document_number_label: {
    es: 'Número de Documento',
    en: 'Document Number',
    fr: 'Numéro de document',
    de: 'Dokumentnummer',
    it: 'Numero documento',
    pt: 'Número do documento',
    nl: 'Documentnummer',
    ru: 'Номер документа',
    ar: 'رقم الوثيقة'
  },

  document_country_label: {
    es: 'País Emisor del Documento',
    en: 'Document Issuing Country',
    fr: 'Pays émetteur du document',
    de: 'Ausstellendes Land',
    it: 'Paese di emissione',
    pt: 'País emissor do documento',
    nl: 'Land van afgifte',
    ru: 'Страна выдачи документа',
    ar: 'بلد إصدار الوثيقة'
  },

  document_expiry_label: {
    es: 'Fecha de Vencimiento del Documento',
    en: 'Document Expiry Date',
    fr: 'Date d\'expiration du document',
    de: 'Ablaufdatum des Dokuments',
    it: 'Data di scadenza del documento',
    pt: 'Data de validade do documento',
    nl: 'Vervaldatum document',
    ru: 'Дата истечения срока действия документа',
    ar: 'تاريخ انتهاء صلاحية الوثيقة'
  },

  email_label: {
    es: 'Correo Electrónico',
    en: 'Email',
    fr: 'E-mail',
    de: 'E-Mail',
    it: 'Email',
    pt: 'E-mail',
    nl: 'E-mail',
    ru: 'Электронная почта',
    ar: 'البريد الإلكتروني'
  },

  city_label: {
    es: 'Ciudad',
    en: 'City',
    fr: 'Ville',
    de: 'Stadt',
    it: 'Città',
    pt: 'Cidade',
    nl: 'Stad',
    ru: 'Город',
    ar: 'المدينة'
  },

  state_label: {
    es: 'Estado/Provincia',
    en: 'State/Province',
    fr: 'État/Province',
    de: 'Bundesland/Provinz',
    it: 'Stato/Provincia',
    pt: 'Estado/Província',
    nl: 'Staat/Provincie',
    ru: 'Штат/Провинция',
    ar: 'الولاية/المحافظة'
  },

  postal_code_label: {
    es: 'Código Postal',
    en: 'Postal Code',
    fr: 'Code postal',
    de: 'Postleitzahl',
    it: 'Codice postale',
    pt: 'Código postal',
    nl: 'Postcode',
    ru: 'Почтовый индекс',
    ar: 'الرمز البريدي'
  },

  country_label: {
    es: 'País',
    en: 'Country',
    fr: 'Pays',
    de: 'Land',
    it: 'Paese',
    pt: 'País',
    nl: 'Land',
    ru: 'Страна',
    ar: 'البلد'
  },

  employment_status_label: {
    es: 'Estado Laboral',
    en: 'Employment Status',
    fr: 'Statut d\'emploi',
    de: 'Beschäftigungsstatus',
    it: 'Stato occupazionale',
    pt: 'Status de emprego',
    nl: 'Arbeidsstatus',
    ru: 'Статус занятости',
    ar: 'حالة التوظيف'
  },

  occupation_label: {
    es: 'Ocupación (SOC)',
    en: 'Occupation (SOC)',
    fr: 'Profession (SOC)',
    de: 'Beruf (SOC)',
    it: 'Occupazione (SOC)',
    pt: 'Ocupação (SOC)',
    nl: 'Beroep (SOC)',
    ru: 'Профессия (SOC)',
    ar: 'المهنة (SOC)'
  },

  occupation_placeholder: {
    es: 'Busque y seleccione el código SOC',
    en: 'Search and select SOC code',
    fr: 'Recherchez et sélectionnez le code SOC',
    de: 'Suchen und wählen Sie den SOC-Code',
    it: 'Cerca e seleziona il codice SOC',
    pt: 'Pesquise e selecione o código SOC',
    nl: 'Zoek en selecteer SOC-code',
    ru: 'Найдите и выберите код SOC',
    ar: 'ابحث واختر رمز SOC'
  },

  employer_name_label: {
    es: 'Nombre del Empleador',
    en: 'Employer Name',
    fr: 'Nom de l\'employeur',
    de: 'Name des Arbeitgebers',
    it: 'Nome del datore di lavoro',
    pt: 'Nome do empregador',
    nl: 'Naam werkgever',
    ru: 'Название работодателя',
    ar: 'اسم صاحب العمل'
  },

  job_title_label: {
    es: 'Título del Puesto',
    en: 'Job Title',
    fr: 'Titre du poste',
    de: 'Berufsbezeichnung',
    it: 'Titolo professionale',
    pt: 'Título do cargo',
    nl: 'Functietitel',
    ru: 'Должность',
    ar: 'المسمى الوظيفي'
  },

  income_source_label: {
    es: 'Fuente de Ingresos (Opcional)',
    en: 'Income Source (Optional)',
    fr: 'Source de revenus (Optionnel)',
    de: 'Einkommensquelle (Optional)',
    it: 'Fonte di reddito (Opzionale)',
    pt: 'Fonte de renda (Opcional)',
    nl: 'Inkomensbron (Optioneel)',
    ru: 'Источник дохода (Необязательно)',
    ar: 'مصدر الدخل (اختياري)'
  },

  tax_number_label: {
    es: 'Número de Identificación Fiscal',
    en: 'Tax ID Number',
    fr: 'Numéro fiscal',
    de: 'Steuernummer',
    it: 'Numero fiscale',
    pt: 'Número de identificação fiscal',
    nl: 'Fiscaal nummer',
    ru: 'Налоговый номер',
    ar: 'رقم التعريف الضريبي'
  },

  is_pep_label: {
    es: '¿Es Persona Políticamente Expuesta (PEP)?',
    en: 'Is Politically Exposed Person (PEP)?',
    fr: 'Est-ce une personne politiquement exposée (PPE) ?',
    de: 'Ist politisch exponierte Person (PEP)?',
    it: 'È una persona politicamente esposta (PEP)?',
    pt: 'É uma Pessoa Politicamente Exposta (PEP)?',
    nl: 'Is politiek prominente persoon (PEP)?',
    ru: 'Является ли политически значимым лицом (PEP)?',
    ar: 'هل هو شخص مكشوف سياسيًا (PEP)؟'
  },

  is_pep_tooltip: {
    es: 'Una Persona Políticamente Expuesta (PEP) es alguien que ocupa o ha ocupado un cargo público importante, como funcionario gubernamental, líder político o ejecutivo de empresa estatal.',
    en: 'A Politically Exposed Person (PEP) is someone who holds or has held a prominent public position, such as a government official, political leader, or state-owned enterprise executive.',
    fr: 'Une Personne Politiquement Exposée (PPE) est quelqu\'un qui occupe ou a occupé une fonction publique importante, comme un fonctionnaire gouvernemental, un dirigeant politique ou un cadre d\'entreprise publique.',
    de: 'Eine politisch exponierte Person (PEP) ist jemand, der eine prominente öffentliche Position innehat oder innehatte, wie z.B. ein Regierungsbeamter, politischer Führer oder Führungskraft eines Staatsunternehmens.',
    it: 'Una Persona Politicamente Esposta (PEP) è qualcuno che ricopre o ha ricoperto una posizione pubblica di rilievo, come un funzionario governativo, un leader politico o un dirigente di un\'impresa statale.',
    pt: 'Uma Pessoa Politicamente Exposta (PEP) é alguém que ocupa ou ocupou um cargo público proeminente, como funcionário governamental, líder político ou executivo de empresa estatal.',
    nl: 'Een politiek prominente persoon (PEP) is iemand die een prominente publieke functie bekleedt of heeft bekleed, zoals een overheidsfunctionaris, politiek leider of directeur van een staatsbedrijf.',
    ru: 'Политически значимое лицо (PEP) - это тот, кто занимает или занимал видную государственную должность, например, государственный чиновник, политический лидер или руководитель государственного предприятия.',
    ar: 'الشخص المكشوف سياسيًا (PEP) هو الشخص الذي يشغل أو شغل منصبًا عامًا بارزًا، مثل مسؤول حكومي أو قائد سياسي أو تنفيذي في مؤسسة مملوكة للدولة.'
  },

  yes: {
    es: 'Sí',
    en: 'Yes',
    fr: 'Oui',
    de: 'Ja',
    it: 'Sì',
    pt: 'Sim',
    nl: 'Ja',
    ru: 'Да',
    ar: 'نعم'
  },

  no: {
    es: 'No',
    en: 'No',
    fr: 'Non',
    de: 'Nein',
    it: 'No',
    pt: 'Não',
    nl: 'Nee',
    ru: 'Нет',
    ar: 'لا'
  },

  // STEP 6: Representative Documents
  step6_title: {
    es: 'Documentos del Representante',
    en: 'Representative Documents',
    fr: 'Documents du représentant',
    de: 'Vertreterdokumente',
    it: 'Documenti del rappresentante',
    pt: 'Documentos do representante',
    nl: 'Documenten vertegenwoordiger',
    ru: 'Документы представителя',
    ar: 'وثائق الممثل'
  },

  step6_subtitle_id_card: {
    es: 'Suba los documentos del representante (5 documentos requeridos para tarjeta de identidad)',
    en: 'Upload the representative\'s documents (5 documents required for ID card)',
    fr: 'Téléchargez les documents du représentant (5 documents requis pour carte d\'identité)',
    de: 'Laden Sie die Dokumente des Vertreters hoch (5 Dokumente für Personalausweis erforderlich)',
    it: 'Carica i documenti del rappresentante (5 documenti richiesti per carta d\'identità)',
    pt: 'Carregue os documentos do representante (5 documentos necessários para carteira de identidade)',
    nl: 'Upload de documenten van de vertegenwoordiger (5 documenten vereist voor identiteitskaart)',
    ru: 'Загрузите документы представителя (требуется 5 документов для удостоверения личности)',
    ar: 'قم بتحميل وثائق الممثل (5 وثائق مطلوبة لبطاقة الهوية)'
  },

  step6_subtitle_passport: {
    es: 'Suba los documentos del representante (4 documentos requeridos para pasaporte)',
    en: 'Upload the representative\'s documents (4 documents required for passport)',
    fr: 'Téléchargez les documents du représentant (4 documents requis pour passeport)',
    de: 'Laden Sie die Dokumente des Vertreters hoch (4 Dokumente für Reisepass erforderlich)',
    it: 'Carica i documenti del rappresentante (4 documenti richiesti per passaporto)',
    pt: 'Carregue os documentos do representante (4 documentos necessários para passaporte)',
    nl: 'Upload de documenten van de vertegenwoordiger (4 documenten vereist voor paspoort)',
    ru: 'Загрузите документы представителя (требуется 4 документа для паспорта)',
    ar: 'قم بتحميل وثائق الممثل (4 وثائق مطلوبة لجواز السفر)'
  },

  id_front_label: {
    es: 'Frente de la Tarjeta de Identidad',
    en: 'ID Card Front',
    fr: 'Recto de la carte d\'identité',
    de: 'Vorderseite des Ausweises',
    it: 'Fronte della carta d\'identità',
    pt: 'Frente da carteira de identidade',
    nl: 'Voorkant identiteitskaart',
    ru: 'Лицевая сторона удостоверения личности',
    ar: 'الجهة الأمامية لبطاقة الهوية'
  },

  id_back_label: {
    es: 'Reverso de la Tarjeta de Identidad',
    en: 'ID Card Back',
    fr: 'Verso de la carte d\'identité',
    de: 'Rückseite des Ausweises',
    it: 'Retro della carta d\'identità',
    pt: 'Verso da carteira de identidade',
    nl: 'Achterkant identiteitskaart',
    ru: 'Обратная сторона удостоверения личности',
    ar: 'الجهة الخلفية لبطاقة الهوية'
  },

  passport_label: {
    es: 'Pasaporte (ambas páginas)',
    en: 'Passport (both pages)',
    fr: 'Passeport (deux pages)',
    de: 'Reisepass (beide Seiten)',
    it: 'Passaporto (entrambe le pagine)',
    pt: 'Passaporte (ambas as páginas)',
    nl: 'Paspoort (beide pagina\'s)',
    ru: 'Паспорт (обе страницы)',
    ar: 'جواز السفر (كلا الصفحتين)'
  },

  id_card_front_label: {
    es: 'Frente de la Tarjeta de Identidad',
    en: 'ID Card Front',
    fr: 'Recto de la carte d\'identité',
    de: 'Vorderseite des Ausweises',
    it: 'Fronte della carta d\'identità',
    pt: 'Frente da carteira de identidade',
    nl: 'Voorkant identiteitskaart',
    ru: 'Лицевая сторона удостоверения личности',
    ar: 'الجهة الأمامية لبطاقة الهوية'
  },

  id_card_back_label: {
    es: 'Reverso de la Tarjeta de Identidad',
    en: 'ID Card Back',
    fr: 'Verso de la carte d\'identité',
    de: 'Rückseite des Ausweises',
    it: 'Retro della carta d\'identità',
    pt: 'Verso da carteira de identidade',
    nl: 'Achterkant identiteitskaart',
    ru: 'Обратная сторона удостоверения личности',
    ar: 'الجهة الخلفية لبطاقة الهوية'
  },

  driving_license_front_label: {
    es: 'Frente de la Licencia de Conducir',
    en: 'Driving License Front',
    fr: 'Recto du permis de conduire',
    de: 'Vorderseite des Führerscheins',
    it: 'Fronte della patente',
    pt: 'Frente da carteira de motorista',
    nl: 'Voorkant rijbewijs',
    ru: 'Лицевая сторона водительских прав',
    ar: 'الجهة الأمامية لرخصة القيادة'
  },

  driving_license_back_label: {
    es: 'Reverso de la Licencia de Conducir',
    en: 'Driving License Back',
    fr: 'Verso du permis de conduire',
    de: 'Rückseite des Führerscheins',
    it: 'Retro della patente',
    pt: 'Verso da carteira de motorista',
    nl: 'Achterkant rijbewijs',
    ru: 'Обратная сторона водительских прав',
    ar: 'الجهة الخلفية لرخصة القيادة'
  },

  selfie_label: {
    es: 'Selfie con Documento de Identidad',
    en: 'Selfie with ID Document',
    fr: 'Selfie avec document d\'identité',
    de: 'Selfie mit Ausweis',
    it: 'Selfie con documento d\'identità',
    pt: 'Selfie com documento de identidade',
    nl: 'Selfie met identiteitsbewijs',
    ru: 'Селфи с документом',
    ar: 'صورة شخصية مع وثيقة الهوية'
  },

  selfie_with_id_label: {
    es: 'Selfie con Documento de Identidad',
    en: 'Selfie with ID Document',
    fr: 'Selfie avec document d\'identité',
    de: 'Selfie mit Ausweis',
    it: 'Selfie con documento d\'identità',
    pt: 'Selfie com documento de identidade',
    nl: 'Selfie met identiteitsbewijs',
    ru: 'Селфи с документом',
    ar: 'صورة شخصية مع وثيقة الهوية'
  },

  utility_bill_label: {
    es: 'Comprobante de Domicilio (Factura de Servicios)',
    en: 'Proof of Address (Utility Bill)',
    fr: 'Justificatif de domicile (Facture)',
    de: 'Adressnachweis (Rechnung)',
    it: 'Prova di residenza (Bolletta)',
    pt: 'Comprovante de residência (Conta de serviços)',
    nl: 'Adresbewijs (Rekening)',
    ru: 'Подтверждение адреса (Счет за коммунальные услуги)',
    ar: 'إثبات العنوان (فاتورة خدمات)'
  },

  proof_of_address_label: {
    es: 'Comprobante de Domicilio',
    en: 'Proof of Address',
    fr: 'Justificatif de domicile',
    de: 'Adressnachweis',
    it: 'Prova di residenza',
    pt: 'Comprovante de residência',
    nl: 'Adresbewijs',
    ru: 'Подтверждение адреса',
    ar: 'إثبات العنوان'
  },

  bank_statement_label: {
    es: 'Extracto Bancario',
    en: 'Bank Statement',
    fr: 'Relevé bancaire',
    de: 'Kontoauszug',
    it: 'Estratto conto bancario',
    pt: 'Extrato bancário',
    nl: 'Bankafschrift',
    ru: 'Банковская выписка',
    ar: 'كشف حساب بنكي'
  },

  // STEP 7: Terms and Submit
  step7_title: {
    es: 'Términos y Condiciones',
    en: 'Terms and Conditions',
    fr: 'Termes et conditions',
    de: 'Geschäftsbedingungen',
    it: 'Termini e condizioni',
    pt: 'Termos e condições',
    nl: 'Algemene voorwaarden',
    ru: 'Условия и положения',
    ar: 'الشروط والأحكام'
  },

  step7_subtitle: {
    es: 'Revise la información y acepte los términos y condiciones',
    en: 'Review the information and accept the terms and conditions',
    fr: 'Vérifiez les informations et acceptez les termes et conditions',
    de: 'Überprüfen Sie die Informationen und akzeptieren Sie die Geschäftsbedingungen',
    it: 'Rivedi le informazioni e accetta i termini e condizioni',
    pt: 'Revise as informações e aceite os termos e condições',
    nl: 'Controleer de informatie en accepteer de algemene voorwaarden',
    ru: 'Проверьте информацию и примите условия и положения',
    ar: 'راجع المعلومات واقبل الشروط والأحكام'
  },

  review_summary: {
    es: 'Resumen de Información',
    en: 'Information Summary',
    fr: 'Résumé des informations',
    de: 'Informationszusammenfassung',
    it: 'Riepilogo informazioni',
    pt: 'Resumo das informações',
    nl: 'Informatiesamenvatting',
    ru: 'Сводка информации',
    ar: 'ملخص المعلومات'
  },

  company_information: {
    es: 'Información de la Empresa',
    en: 'Company Information',
    fr: 'Informations sur l\'entreprise',
    de: 'Unternehmensinformationen',
    it: 'Informazioni sull\'azienda',
    pt: 'Informações da empresa',
    nl: 'Bedrijfsinformatie',
    ru: 'Информация о компании',
    ar: 'معلومات الشركة'
  },

  representatives_information: {
    es: 'Representantes',
    en: 'Representatives',
    fr: 'Représentants',
    de: 'Vertreter',
    it: 'Rappresentanti',
    pt: 'Representantes',
    nl: 'Vertegenwoordigers',
    ru: 'Представители',
    ar: 'الممثلون'
  },

  open_terms_button: {
    es: '📄 Abrir Términos y Condiciones',
    en: '📄 Open Terms and Conditions',
    fr: '📄 Ouvrir les termes et conditions',
    de: '📄 Geschäftsbedingungen öffnen',
    it: '📄 Apri termini e condizioni',
    pt: '📄 Abrir termos e condições',
    nl: '📄 Open algemene voorwaarden',
    ru: '📄 Открыть условия и положения',
    ar: '📄 افتح الشروط والأحكام'
  },

  accept_terms_checkbox: {
    es: 'He leído y acepto los Términos y Condiciones de BELOBABA',
    en: 'I have read and accept the BELOBABA Terms and Conditions',
    fr: 'J\'ai lu et j\'accepte les termes et conditions de BELOBABA',
    de: 'Ich habe die BELOBABA Geschäftsbedingungen gelesen und akzeptiert',
    it: 'Ho letto e accetto i termini e condizioni di BELOBABA',
    pt: 'Li e aceito os Termos e Condições da BELOBABA',
    nl: 'Ik heb de algemene voorwaarden van BELOBABA gelezen en geaccepteerd',
    ru: 'Я прочитал и принимаю условия и положения BELOBABA',
    ar: 'لقد قرأت وأوافق على شروط وأحكام BELOBABA'
  },

  must_accept_terms: {
    es: 'Debe aceptar los términos y condiciones para continuar',
    en: 'You must accept the terms and conditions to continue',
    fr: 'Vous devez accepter les termes et conditions pour continuer',
    de: 'Sie müssen die Geschäftsbedingungen akzeptieren, um fortzufahren',
    it: 'Devi accettare i termini e condizioni per continuare',
    pt: 'Você deve aceitar os termos e condições para continuar',
    nl: 'U moet de algemene voorwaarden accepteren om door te gaan',
    ru: 'Вы должны принять условия и положения, чтобы продолжить',
    ar: 'يجب عليك قبول الشروط والأحكام للمتابعة'
  },

  submitting_form: {
    es: 'Enviando formulario...',
    en: 'Submitting form...',
    fr: 'Envoi du formulaire...',
    de: 'Formular wird gesendet...',
    it: 'Invio del modulo...',
    pt: 'Enviando formulário...',
    nl: 'Formulier verzenden...',
    ru: 'Отправка формы...',
    ar: 'جار إرسال النموذج...'
  },

  success_message: {
    es: '¡Registro completado con éxito! Redirigiendo al inicio de sesión...',
    en: 'Registration completed successfully! Redirecting to login...',
    fr: 'Inscription réussie! Redirection vers la connexion...',
    de: 'Registrierung erfolgreich abgeschlossen! Weiterleitung zur Anmeldung...',
    it: 'Registrazione completata con successo! Reindirizzamento al login...',
    pt: 'Registro concluído com sucesso! Redirecionando para login...',
    nl: 'Registratie succesvol voltooid! Doorsturen naar inloggen...',
    ru: 'Регистрация успешно завершена! Перенаправление на вход...',
    ar: 'تم التسجيل بنجاح! جارٍ إعادة التوجيه إلى تسجيل الدخول...'
  },

  error_submitting: {
    es: 'Error al enviar el formulario. Por favor, intente nuevamente.',
    en: 'Error submitting form. Please try again.',
    fr: 'Erreur lors de l\'envoi du formulaire. Veuillez réessayer.',
    de: 'Fehler beim Senden des Formulars. Bitte versuchen Sie es erneut.',
    it: 'Errore nell\'invio del modulo. Riprova.',
    pt: 'Erro ao enviar formulário. Por favor, tente novamente.',
    nl: 'Fout bij het verzenden van formulier. Probeer het opnieuw.',
    ru: 'Ошибка при отправке формы. Пожалуйста, попробуйте снова.',
    ar: 'خطأ في إرسال النموذج. يرجى المحاولة مرة أخرى.'
  },

  // Enums - Company Type
  company_type_c_corp_private: {
    es: 'Corporación C (Privada)',
    en: 'C Corporation (Private)',
    fr: 'Société C (Privée)',
    de: 'C Corporation (Privat)',
    it: 'C Corporation (Privata)',
    pt: 'C Corporation (Privada)',
    nl: 'C Corporation (Privaat)',
    ru: 'C Corporation (Частная)',
    ar: 'شركة C (خاصة)'
  },

  company_type_c_corp_public: {
    es: 'Corporación C (Pública)',
    en: 'C Corporation (Public)',
    fr: 'Société C (Publique)',
    de: 'C Corporation (Öffentlich)',
    it: 'C Corporation (Pubblica)',
    pt: 'C Corporation (Pública)',
    nl: 'C Corporation (Publiek)',
    ru: 'C Corporation (Публичная)',
    ar: 'شركة C (عامة)'
  },

  company_type_llc: {
    es: 'Sociedad de Responsabilidad Limitada (LLC)',
    en: 'Limited Liability Company (LLC)',
    fr: 'Société à Responsabilité Limitée (SARL)',
    de: 'Gesellschaft mit beschränkter Haftung (GmbH)',
    it: 'Società a Responsabilità Limitata (SRL)',
    pt: 'Sociedade de Responsabilidade Limitada (LTDA)',
    nl: 'Besloten Vennootschap (BV)',
    ru: 'Общество с ограниченной ответственностью (ООО)',
    ar: 'شركة ذات مسؤولية محدودة'
  },

  company_type_private_company: {
    es: 'Empresa Privada',
    en: 'Private Company',
    fr: 'Entreprise privée',
    de: 'Privatunternehmen',
    it: 'Azienda privata',
    pt: 'Empresa privada',
    nl: 'Privébedrijf',
    ru: 'Частная компания',
    ar: 'شركة خاصة'
  },

  company_type_other: {
    es: 'Otro',
    en: 'Other',
    fr: 'Autre',
    de: 'Sonstiges',
    it: 'Altro',
    pt: 'Outro',
    nl: 'Anders',
    ru: 'Другое',
    ar: 'أخرى'
  },

  // Enums - Business Type
  business_type_services: {
    es: 'Servicios',
    en: 'Services',
    fr: 'Services',
    de: 'Dienstleistungen',
    it: 'Servizi',
    pt: 'Serviços',
    nl: 'Diensten',
    ru: 'Услуги',
    ar: 'خدمات'
  },

  business_type_other: {
    es: 'Otro',
    en: 'Other',
    fr: 'Autre',
    de: 'Sonstiges',
    it: 'Altro',
    pt: 'Outro',
    nl: 'Anders',
    ru: 'Другое',
    ar: 'أخرى'
  },

  // Enums - Relationship Type
  relationship_director: {
    es: 'Director',
    en: 'Director',
    fr: 'Directeur',
    de: 'Direktor',
    it: 'Direttore',
    pt: 'Diretor',
    nl: 'Directeur',
    ru: 'Директор',
    ar: 'مدير'
  },

  relationship_shareholder: {
    es: 'Accionista',
    en: 'Shareholder',
    fr: 'Actionnaire',
    de: 'Aktionär',
    it: 'Azionista',
    pt: 'Acionista',
    nl: 'Aandeelhouder',
    ru: 'Акционер',
    ar: 'مساهم'
  },

  relationship_beneficial_owner: {
    es: 'Beneficiario Final',
    en: 'Beneficial Owner',
    fr: 'Bénéficiaire effectif',
    de: 'Wirtschaftlich Berechtigter',
    it: 'Beneficiario finale',
    pt: 'Beneficiário final',
    nl: 'Uiteindelijk belanghebbende',
    ru: 'Конечный бенефициар',
    ar: 'المالك المستفيد'
  },

  // Enums - Gender
  gender_male: {
    es: 'Masculino',
    en: 'Male',
    fr: 'Masculin',
    de: 'Männlich',
    it: 'Maschile',
    pt: 'Masculino',
    nl: 'Mannelijk',
    ru: 'Мужской',
    ar: 'ذكر'
  },

  gender_female: {
    es: 'Femenino',
    en: 'Female',
    fr: 'Féminin',
    de: 'Weiblich',
    it: 'Femminile',
    pt: 'Feminino',
    nl: 'Vrouwelijk',
    ru: 'Женский',
    ar: 'أنثى'
  },

  // Enums - Document Type
  document_type_id_card: {
    es: 'Tarjeta de Identidad Nacional',
    en: 'National ID Card',
    fr: 'Carte d\'identité nationale',
    de: 'Personalausweis',
    it: 'Carta d\'identità nazionale',
    pt: 'Carteira de identidade nacional',
    nl: 'Nationale identiteitskaart',
    ru: 'Национальное удостоверение личности',
    ar: 'بطاقة الهوية الوطنية'
  },

  document_type_passport: {
    es: 'Pasaporte',
    en: 'Passport',
    fr: 'Passeport',
    de: 'Reisepass',
    it: 'Passaporto',
    pt: 'Passaporte',
    nl: 'Paspoort',
    ru: 'Паспорт',
    ar: 'جواز السفر'
  },

  document_type_driving_license: {
    es: 'Licencia de Conducir',
    en: 'Driver\'s License',
    fr: 'Permis de conduire',
    de: 'Führerschein',
    it: 'Patente di guida',
    pt: 'Carteira de motorista',
    nl: 'Rijbewijs',
    ru: 'Водительские права',
    ar: 'رخصة القيادة'
  },

  // Enums - Employment Status
  employment_employed: {
    es: 'Empleado',
    en: 'Employed',
    fr: 'Employé',
    de: 'Angestellt',
    it: 'Impiegato',
    pt: 'Empregado',
    nl: 'In dienst',
    ru: 'Работает по найму',
    ar: 'موظف'
  },

  employment_self_employed: {
    es: 'Autónomo',
    en: 'Self-Employed',
    fr: 'Indépendant',
    de: 'Selbständig',
    it: 'Lavoratore autonomo',
    pt: 'Autônomo',
    nl: 'Zelfstandige',
    ru: 'Самозанятый',
    ar: 'يعمل لحسابه الخاص'
  },

  employment_unemployed: {
    es: 'Desempleado',
    en: 'Unemployed',
    fr: 'Sans emploi',
    de: 'Arbeitslos',
    it: 'Disoccupato',
    pt: 'Desempregado',
    nl: 'Werkloos',
    ru: 'Безработный',
    ar: 'عاطل عن العمل'
  },

  employment_retired: {
    es: 'Jubilado',
    en: 'Retired',
    fr: 'Retraité',
    de: 'Im Ruhestand',
    it: 'Pensionato',
    pt: 'Aposentado',
    nl: 'Gepensioneerd',
    ru: 'На пенсии',
    ar: 'متقاعد'
  },

  employment_student: {
    es: 'Estudiante',
    en: 'Student',
    fr: 'Étudiant',
    de: 'Student',
    it: 'Studente',
    pt: 'Estudante',
    nl: 'Student',
    ru: 'Студент',
    ar: 'طالب'
  },

  // Search/Filter helpers
  search_placeholder: {
    es: 'Buscar...',
    en: 'Search...',
    fr: 'Rechercher...',
    de: 'Suchen...',
    it: 'Cerca...',
    pt: 'Pesquisar...',
    nl: 'Zoeken...',
    ru: 'Поиск...',
    ar: 'بحث...'
  },

  no_results: {
    es: 'No se encontraron resultados',
    en: 'No results found',
    fr: 'Aucun résultat trouvé',
    de: 'Keine Ergebnisse gefunden',
    it: 'Nessun risultato trovato',
    pt: 'Nenhum resultado encontrado',
    nl: 'Geen resultaten gevonden',
    ru: 'Результатов не найдено',
    ar: 'لم يتم العثور على نتائج'
  },

  select_option: {
    es: 'Seleccione una opción',
    en: 'Select an option',
    fr: 'Sélectionnez une option',
    de: 'Wählen Sie eine Option',
    it: 'Seleziona un\'opzione',
    pt: 'Selecione uma opção',
    nl: 'Selecteer een optie',
    ru: 'Выберите вариант',
    ar: 'حدد خيارًا'
  }
};

// Helper function to get translation
export const getTranslation = (key: string, lang: Language): string => {
  return corporateTranslations[key]?.[lang] || corporateTranslations[key]?.['en'] || key;
};

// Helper function to replace placeholders in translations
export const getTranslationWithPlaceholders = (
  key: string,
  lang: Language,
  placeholders: Record<string, string | number>
): string => {
  let translation = getTranslation(key, lang);

  Object.keys(placeholders).forEach((placeholder) => {
    translation = translation.replace(`{${placeholder}}`, String(placeholders[placeholder]));
  });

  return translation;
};
